import { Component, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TrackingService } from "src/services/tracking.service";
import { CommonModule } from "@angular/common";
import { ToastModule } from "primeng/toast";
import { SpinnerService } from "src/services/spinner.service";
import { MessageService } from "primeng/api";
import { RastreioPublicoResponse } from "@/providers/models/rastreio";
import * as L from "leaflet";


@Component({
    selector: "app-tracking-client",
    standalone: true,
    templateUrl: "./tracking-client.component.html",
    styleUrls: ["./tracking-client.component.scss"],
    imports: [
        ToastModule,
        CommonModule,
    ],
    providers: [
        TrackingService,
        SpinnerService,
        MessageService
    ]
})
export class TrackingClientComponent implements OnInit, OnDestroy {

    private route = inject(ActivatedRoute);
    private trackingService = inject(TrackingService);
    private spinnerService = inject(SpinnerService);
    private messageService = inject(MessageService);

    mapContainer = viewChild<ElementRef<HTMLDivElement>>('mapContainer');

    rastreio = signal<RastreioPublicoResponse | null>(null);
    loading = signal(true);
    expirado = signal(false);
    motoristaChegou = signal(false);

    private map: L.Map | null = null;
    private truckMarker: L.Marker | null = null;
    private timerPolling: any = null;

    /** Coordenadas do destino salvas no carregamento inicial. */
    private destinoLat: number | null = null;
    private destinoLng: number | null = null;

    /** Distância em metros entre dois pontos (Haversine). */
    private distanciaMetros(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6_371_000;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2
            + Math.cos(lat1 * Math.PI / 180)
            * Math.cos(lat2 * Math.PI / 180)
            * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    /** Retorna true quando o motorista está a ≤ 80m do destino. */
    private verificarChegada(motoristaLat: number, motoristaLng: number): boolean {
        if (!motoristaLat || !motoristaLng || !this.destinoLat || !this.destinoLng) return false;
        return this.distanciaMetros(
            motoristaLat, motoristaLng,
            this.destinoLat, this.destinoLng
        ) <= 40;
    }

    ngOnInit() {
        this.configurarIconesPadrao();
        const token = this.route.snapshot.paramMap.get('token');
        if (!token) {
            this.expirado.set(true);
            this.loading.set(false);
            return;
        }
        this.carregarDados(token);
    }

    private configurarIconesPadrao() {
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        });
    }

    async carregarDados(token: string) {
        this.spinnerService.show();
        await this.trackingService.obterRotaDeEntrega(token)
            .then((data) => {
                if (data && data.status && data.data) {
                    this.rastreio.set(data.data ?? null);
                    this.destinoLat = data.data.destinoLatitude;
                    this.destinoLng = data.data.destinoLongitude;
                    this.loading.set(false);

                    this.motoristaChegou.set(
                        this.verificarChegada(data.data.motoristaLatitude, data.data.motoristaLongitude)
                    );

                    setTimeout(() => {
                        this.montarMapa(data.data!);
                        this.iniciarPollingGps(token);
                    }, 50);
                } else {
                    this.expirado.set(true);
                    this.loading.set(false);
                }
            }).catch((error) => {
                this.messageService.add({
                    severity: "error",
                    summary: "Erro!",
                    detail: `Erro.`,
                    life: 5000,
                });
            }).finally(() => this.spinnerService.hide());
    }

    async iniciarPollingGps(token: string) {
        this.spinnerService.show();
        this.timerPolling = setInterval(async () => {
            await this.trackingService.obterPosicaoMotorista(token)
                .then((data) => {
                    if (data && data.status && data.data) {
                        if (this.truckMarker && data.data.motoristaLatitude && data.data.motoristaLongitude) {
                            this.truckMarker.setLatLng([data.data.motoristaLatitude, data.data.motoristaLongitude]);
                        }
                        this.motoristaChegou.set(
                            this.verificarChegada(data.data.motoristaLatitude, data.data.motoristaLongitude)
                        );
                        if (data.data.status === 'Entregue') {
                            this.expirado.set(true);
                            clearInterval(this.timerPolling);
                        }
                    }
                }).catch((error) => {
                    this.messageService.add({
                        severity: "error",
                        summary: "Erro!",
                        detail: `Erro.`,
                        life: 5000,
                    });
                    clearInterval(this.timerPolling)
                }).finally(() => this.spinnerService.hide());
        }, 15000);
    }

    private montarMapa(data: RastreioPublicoResponse) {
        const el = this.mapContainer()?.nativeElement;
        if (!el) return;

        this.map = L.map(el).setView([data.motoristaLatitude, data.motoristaLongitude], 13);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '© OpenStreetMap'
        }).addTo(this.map);

        // Marcador do Local de Instalação (Cliente)
        const iconeCasa = L.divIcon({
            className: 'dest-marker',
            html: '<div style="background:#059669; color:#fff; border-radius:50%; width:32px; height:32px; display:flex; align-items:center; justify-content:center; border:2px solid #fff; box-shadow:0 2px 4px rgba(0,0,0,0.3)">🏠</div>',
            iconSize: [32, 32],
            iconAnchor: [16, 16]
        });

        L.marker([data.destinoLatitude, data.destinoLongitude], { icon: iconeCasa })
            .addTo(this.map)
            .bindPopup('<b>Local de Instalação</b><br>Destino dos equipamentos.');

        // Marcador do Caminhão de Entrega
        const iconeCaminhao = L.divIcon({
            className: 'truck-marker',
            html: '<div style="background:#2563eb; color:#fff; border-radius:50%; width:36px; height:36px; display:flex; align-items:center; justify-content:center; border:2px solid #fff; box-shadow:0 2px 4px rgba(0,0,0,0.3)">🚚</div>',
            iconSize: [36, 36],
            iconAnchor: [18, 18]
        });

        this.truckMarker = L.marker([data.motoristaLatitude, data.motoristaLongitude], { icon: iconeCaminhao })
            .addTo(this.map)
            .bindPopup('<b>Caminhão Solar</b><br>Em trânsito até você.');

        // Enquadra a visão para exibir o caminhão e o destino simultaneamente
        const bounds = L.latLngBounds(
            [data.motoristaLatitude, data.motoristaLongitude],
            [data.destinoLatitude, data.destinoLongitude]
        );
        this.map.fitBounds(bounds, { padding: [40, 40] });
    }

    ngOnDestroy() {
        if (this.timerPolling) clearInterval(this.timerPolling);
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }
}